package net.zagart.ai.core.feature.story

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import net.zagart.ai.core.client.AiChatMessage
import net.zagart.ai.core.client.AiChatRequest
import net.zagart.ai.core.client.AiChatRole
import net.zagart.ai.core.client.AiClient

@Serializable
private data class StoryResponse(
    val narrative: String,
    val choices: List<ChoiceData> = emptyList(),
    val ending: Boolean = false,
    val epilogue: String? = null
)

@Serializable
private data class ChoiceData(
    val id: String,
    val text: String,
    val summary: String? = null
)

class StoryEngine(
    private val client: AiClient,
    private val config: StoryConfig = StoryConfig()
) {
    private val json = Json { ignoreUnknownKeys = true }
    private val history = mutableListOf<AiChatMessage>()

    suspend fun start(): StoryState {
        println("[story] start() genre=${config.genre} lang=${config.language} wishes=${config.plotWishes}")
        history.clear()
        val prompt = config.systemPrompt
            .replace("{genre}", config.genre)
            .replace("{language}", config.language)
            .replace("{plotWishes}", config.plotWishes)
        history.add(AiChatMessage(AiChatRole.system, prompt))
        history.add(AiChatMessage(AiChatRole.user, "Begin the story."))
        println("[story] start() history size=${history.size}")
        return sendAndParse()
    }

    suspend fun choose(choice: StoryChoice): StoryState {
        println("[story] choose() id=${choice.id} text=${choice.text}")
        history.add(AiChatMessage(AiChatRole.user, "I choose: ${choice.text}"))
        return sendAndParse()
    }

    fun reset() {
        history.clear()
    }

    val currentHistory: List<AiChatMessage>
        get() = history.toList()

    private suspend fun sendAndParse(): StoryState {
        println("[story] sendAndParse() calling client.chat...")
        val response = client.chat(AiChatRequest(
            model = config.model,
            messages = history.toList(),
            temperature = config.temperature,
            maxTokens = config.maxHistoryTokens
        ))
        println("[story] sendAndParse() client.chat returned, choices=${response.choices.size}")
        val choice = response.choices.firstOrNull()
        println("[story] sendAndParse() first choice=${choice?.index} message=${choice?.message?.content?.take(50)}...")
        val content = choice?.message?.content ?: error("Empty AI response")
        println("[story] sendAndParse() content length=${content.length}")
        history.add(AiChatMessage(AiChatRole.assistant, content))
        val storyResponse = json.decodeFromString<StoryResponse>(content)
        println("[story] sendAndParse() parsed: narrative=${storyResponse.narrative.take(40)}... ending=${storyResponse.ending} choices=${storyResponse.choices.size}")
        val node = if (storyResponse.ending) {
            StoryNode.Ending(storyResponse.narrative, storyResponse.epilogue)
        } else {
            StoryNode.Narrative(
                storyResponse.narrative,
                storyResponse.choices.map { StoryChoice(it.id, it.text, it.summary) }
            )
        }
        return StoryState(node, history.toList(), storyResponse.ending)
    }
}
